import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Sparkles, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL, API, getOrCreateSessionId, api } from "../lib/api";

const LINK_RE = /(https?:\/\/[^\s)]+|\/(?:request|insurance|troubleshooting|services|local-services|spare-parts)(?:\/[a-z0-9-]+)?)/gi;

function renderContent(text) {
  if (!text) return null;
  return text.split(LINK_RE).map((part, idx) => {
    if (!part) return null;
    if (/^https?:\/\//i.test(part)) {
      return <a key={idx} href={part} target="_blank" rel="noreferrer" className="underline font-semibold text-[#16A34A] hover:text-[#15803D]">{part}</a>;
    }
    if (/^\/(request|insurance|troubleshooting|services|local-services|spare-parts)/i.test(part)) {
      return <Link key={idx} to={part} className="underline font-semibold text-[#16A34A] hover:text-[#15803D]">{part}</Link>;
    }
    return <span key={idx}>{part}</span>;
  });
}

export default function AIReceptionist() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "G'day — I'm Rosie, the AI receptionist for Nomad Caravan Repairs (Bowen, Whitsundays). What's going on with your van?" }
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [leadReady, setLeadReady] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [lead, setLead] = useState({ name:"", email:"", phone:"", caravan_type:"", issue:"", budget_range:"", urgency:"" });
  const [submittedLead, setSubmittedLead] = useState(null);
  const sessionId = getOrCreateSessionId();
  const scrollRef = useRef(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-ai-receptionist", handler);
    return () => window.removeEventListener("open-ai-receptionist", handler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const r = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!r.ok || !r.body) throw new Error("stream failed");
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const payload = p.slice(5).trim();
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.done) {
              setStreaming(false);
              if (obj.lead_ready) {
                setLeadReady(true);
              }
              if (obj.feedback_captured) {
                toast.success("Thanks for your feedback — it helps us improve!");
              }
              // Strip hidden control tags from displayed message
              setMessages((m) => {
                const copy = [...m];
                const cleaned = copy[copy.length - 1].content
                  .replace(/\[LEAD_READY\]/g, "")
                  .replace(/\[FEEDBACK[^\]]*\][\s\S]*?\[\/FEEDBACK\]/g, "")
                  .trim();
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: cleaned };
                return copy;
              });
            }
            if (obj.error) toast.error("Chat error: " + obj.error);
          } catch {}
        }
      }
    } catch (e) {
      toast.error("Network error talking to Rosie");
    } finally {
      setStreaming(false);
    }
  };

  const submitLead = async () => {
    if (!lead.name || !lead.email || !lead.issue) {
      toast.error("Name, email and issue are required");
      return;
    }
    try {
      const r = await api.post("/chat/lead", { ...lead, session_id: sessionId });
      setSubmittedLead(r.data);
      setLeadFormOpen(false);
      toast.success("Thanks — the team will be in touch.");
    } catch (e) {
      toast.error("Could not submit. Try again.");
    }
  };

  return (
    <div className="fixed bottom-20 right-6 z-[100] flex flex-col items-end">
      {open && (
        <div className="w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100dvh-7rem)] bg-white border border-zinc-950 shadow-2xl mb-4 flex flex-col overflow-hidden">
          <div className="bg-zinc-950 text-white p-5 flex items-center justify-between border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#FFCC00] text-zinc-950 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-bold text-sm tracking-tight">ROSIE</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400"></span>AI Receptionist · Online
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-zinc-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${m.role}-${i}`} className={`max-w-[85%] p-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#002FA7] text-white" : "bg-white border border-zinc-200 text-zinc-900"}`}>
                  {m.content ? renderContent(m.content) : (streaming && i === messages.length - 1 ? <span className="inline-block w-1.5 h-4 bg-zinc-950 caret align-middle" /> : null)}
                </div>
              </div>
            ))}
            {leadReady && !leadFormOpen && !submittedLead && (
              <button onClick={() => setLeadFormOpen(true)} data-testid="chat-submit-lead" className="w-full bg-[#FFCC00] text-zinc-950 px-4 py-3 font-semibold hover:bg-yellow-300 transition-colors flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Confirm details & request callback
              </button>
            )}
            {submittedLead && (
              <div className="bg-zinc-950 text-white p-4 text-sm">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFCC00] mb-2">Booked in · Score {submittedLead.qualification_score}/100</div>
                <p>Thanks {submittedLead.name}. The workshop has your details — expect a callback within one business day.</p>
              </div>
            )}
          </div>

          {leadFormOpen ? (
            <div className="border-t border-zinc-200 bg-white p-4 overflow-y-auto" style={{maxHeight: "60%"}}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Confirm your details</div>
              <div className="space-y-2">
                {[
                  {k:"name", p:"Your name *"},
                  {k:"email", p:"Email *"},
                  {k:"phone", p:"Phone"},
                  {k:"caravan_type", p:"Caravan make / model / year"},
                  {k:"issue", p:"What's the issue? *"},
                  {k:"urgency", p:"Urgency (this week / month / exploring)"},
                ].map(({k,p}) => (
                  <input key={k} value={lead[k]} placeholder={p} onChange={(e) => setLead({...lead, [k]: e.target.value})} data-testid={`lead-${k}`} className="w-full border border-zinc-300 px-3 py-2.5 text-base focus:outline-none focus:border-zinc-950" />
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setLeadFormOpen(false)} data-testid="lead-cancel" className="flex-1 border border-zinc-300 py-2 text-sm">Back</button>
                <button onClick={submitLead} data-testid="lead-submit" className="flex-1 bg-[#002FA7] text-white py-2 text-sm font-semibold hover:bg-[#002075]">Submit</button>
              </div>
            </div>
          ) : (
            <div className="border-t border-zinc-200 bg-white p-3 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="chat-input"
                placeholder={streaming ? "Rosie is typing..." : "Type your message…"}
                disabled={streaming}
                className="flex-1 min-w-0 border border-zinc-300 px-3 py-3 text-base focus:outline-none focus:border-zinc-950 disabled:bg-zinc-50"
              />
              <button onClick={send} disabled={streaming || !input.trim()} data-testid="chat-send" className="bg-zinc-950 text-white px-4 hover:bg-[#002FA7] transition-colors disabled:opacity-50 shrink-0">
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-trigger"
        className="bg-[#16A34A] text-white px-5 py-4 shadow-2xl hover:bg-[#15803D] hover:-translate-y-1 transition-all flex items-center gap-3 font-semibold border-2 border-white"
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
        <span className="font-display tracking-tight">{open ? "Close" : "Ask Rosie"}</span>
      </button>
    </div>
  );
}
